import { useMemo } from 'react';
import { SALESORDERS } from 'constants/Entities';
import { getMappedFilters } from 'utils/filters';
import { FILTERS_TO_IGNORE_BASE } from '../constants';

/**
 * Custom hook for processing and managing entity filters
 *
 * This hook handles the complex logic of determining which filters to ignore,
 * mapping visible and quick filters, and computing default filters based on
 * various conditions like entity type, cross-schema filters, and storage preferences.
 *
 * @param {Object} entity - The entity object containing entity type information
 * @param {boolean} enableSalesOrdersTypes - Flag to enable sales order type filtering
 * @param {string[]} dontIgnoreTheseFilters - Array of filter names that should not be ignored
 * @param {Array} visibleFilters - Array of visible filters to be mapped
 * @param {Array} quickFilters - Array of quick filters to be mapped
 * @param {Object} state - Component state containing filter and schema information
 * @param {Array} state.updatedDefaultFilters - Current default filters from state
 * @param {Object} state.crossSchema - Cross-schema configuration for filters
 * @param {boolean} hasCrossFilters - Flag indicating if cross-schema filters are enabled
 * @param {Function} getDefaultFilters - Function to retrieve default filters with cross-schema support
 * @param {boolean} avoidStorage - Flag to skip storage-related filter operations
 *
 * @returns {Object} An object containing processed filter data:
 * @returns {string[]} filtersToIgnore - Array of filter names that should be ignored
 * @returns {Array} mappedVisibleFilters - Mapped visible filters for the current entity
 * @returns {Array} mappedQuickFilters - Mapped quick filters for the current entity
 * @returns {Array} newDefaultFilters - Computed default filters after processing
 */
const useFilterProcessing = (
    entity,
    enableSalesOrdersTypes,
    dontIgnoreTheseFilters,
    visibleFilters,
    quickFilters,
    state,
    hasCrossFilters,
    getDefaultFilters,
    avoidStorage,
) => {
    /**
     * Compute the list of filters that should be ignored during processing
     *
     * This memoized value builds a list of filters to ignore by:
     * 1. Starting with the base filters that are always ignored
     * 2. Adding 'salesOrderType' filter if sales order types are enabled for sales order entities
     * 3. Removing any filters that are explicitly marked as "don't ignore"
     */
    const filtersToIgnore = useMemo(() => {
        // Start with base filters that should always be ignored
        let newFiltersToIgnore = [...FILTERS_TO_IGNORE_BASE];

        // Add salesOrderType to ignored filters for sales order entities when feature is enabled
        if (enableSalesOrdersTypes && entity.entity === SALESORDERS.entity) {
            newFiltersToIgnore.push('salesOrderType');
        }

        // Remove any filters that are explicitly marked as "don't ignore"
        if (dontIgnoreTheseFilters?.length > 0) {
            newFiltersToIgnore = newFiltersToIgnore.filter(
                (current) => !dontIgnoreTheseFilters.includes(current),
            );
        }

        return newFiltersToIgnore;
    }, [dontIgnoreTheseFilters, enableSalesOrdersTypes, entity.entity]);

    /**
     * Map visible filters to the current entity context
     *
     * This transforms the visible filters to be compatible with the current entity type,
     * ensuring that filter configurations are properly adapted for the specific entity.
     */
    const mappedVisibleFilters = useMemo(
        () => getMappedFilters(visibleFilters, entity.entity),
        [entity, visibleFilters],
    );

    /**
     * Map quick filters to the current entity context
     *
     * Similar to visible filters, this ensures quick filters are properly mapped
     * and compatible with the current entity type.
     */
    const mappedQuickFilters = useMemo(
        () => getMappedFilters(quickFilters, entity.entity),
        [entity, quickFilters],
    );

    /**
     * Compute the final default filters after processing
     *
     * This memoized value performs the following operations:
     * 1. Filters out any default filters that should be ignored
     * 2. If storage is not avoided and cross-filters are enabled, applies cross-schema logic
     * 3. Returns the final processed default filters
     */
    const newDefaultFilters = useMemo(() => {
        // Start by filtering out any filters that should be ignored
        let filters = state.updatedDefaultFilters.filter(
            (current) => !filtersToIgnore.includes(current),
        );

        // Apply cross-schema filtering logic if storage is enabled and cross-filters are available
        if (!avoidStorage && hasCrossFilters) {
            filters = getDefaultFilters({
                crossSchema: state.crossSchema,
                defaultFilters: filters,
                filtersToIgnore,
            });
        }

        return filters;
    }, [
        filtersToIgnore,
        getDefaultFilters,
        hasCrossFilters,
        state.crossSchema,
        state.updatedDefaultFilters,
        avoidStorage,
    ]);

    return {
        filtersToIgnore,
        mappedVisibleFilters,
        mappedQuickFilters,
        newDefaultFilters,
    };
};

export default useFilterProcessing;
